/**
 * Geo primitives — pure. Haversine distance and nearest-feature search feed the
 * deterministic water/access rules from seeded spatial data.
 */

export interface LngLat {
  lng: number;
  lat: number;
}

const R = 6_371_000; // Earth radius (m)
const toRad = (d: number): number => (d * Math.PI) / 180;

/** Great-circle distance between two points, in metres. */
export function haversineMeters(a: LngLat, b: LngLat): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface Nearest<T> {
  item: T;
  distanceM: number;
}

/** Nearest item to `from` by haversine distance (undefined for an empty list). */
export function nearest<T>(
  from: LngLat,
  items: readonly T[],
  coordOf: (item: T) => LngLat,
): Nearest<T> | undefined {
  let best: Nearest<T> | undefined;
  for (const item of items) {
    const d = haversineMeters(from, coordOf(item));
    if (!best || d < best.distanceM) best = { item, distanceM: Math.round(d) };
  }
  return best;
}

/** Items within `radiusM` of `from`, nearest first. */
export function within<T>(
  from: LngLat,
  items: readonly T[],
  radiusM: number,
  coordOf: (item: T) => LngLat,
): Nearest<T>[] {
  return items
    .map((item) => ({ item, distanceM: Math.round(haversineMeters(from, coordOf(item))) }))
    .filter((n) => n.distanceM <= radiusM)
    .sort((a, b) => a.distanceM - b.distanceM);
}
